import { ComparisonCondition, Condition } from '@the-real-insight/tri-model';

/**
 * The simple conditions view of an alert rule.
 *
 * The server's model: a flat top-level list of conditions is an AND; OR exists only as an explicit
 * `OrCondition`. The simple view shows one row per comparison and one switch "all / any":
 *
 *  - "all": the rows are the flat top-level list (a single row is just the row);
 *  - "any": one top-level `OrCondition` holding the rows.
 *
 * Anything the rows cannot express (nested groups, an OR beside other conditions, …) is
 * `advanced`: shown read-only and stored untouched unless the user removes it.
 */
export type MatchMode = 'all' | 'any';

export type ConditionsView =
    | { kind: 'simple'; match: MatchMode; rows: any[] }
    | { kind: 'advanced' };

const typeOf = (condition: any): string | undefined => condition?.conditionType;

const isComparison = (condition: any): boolean =>
    typeOf(condition) === 'Comparison' || (condition instanceof ComparisonCondition);

const isGroup = (condition: any, kind: 'AndCondition' | 'OrCondition'): boolean =>
    typeOf(condition) === kind && Array.isArray(condition.conditions);

/** Comparisons of an AND tree (groups of groups of comparisons), flattened; `null` if anything else is in it. */
const flattenAnd = (condition: any): any[] | null => {
    if (isComparison(condition)) {
        return [condition];
    }

    if (!isGroup(condition, 'AndCondition')) {
        return null;
    }

    const rows: any[] = [];

    for (const child of condition.conditions) {
        const flat = flattenAnd(child);

        if (!flat) {
            return null;
        }

        rows.push(...flat);
    }

    return rows;
};

export const viewOfConditions = (conditions: any[] | undefined | null): ConditionsView => {
    const list = conditions ?? [];

    if (list.length === 1 && isGroup(list[0], 'OrCondition')) {
        const children = list[0].conditions as any[];

        return children.every(isComparison)
            ? { kind: 'simple', match: 'any', rows: children }
            : { kind: 'advanced' };
    }

    const rows: any[] = [];

    for (const condition of list) {
        const flat = flattenAnd(condition);

        if (!flat) {
            return { kind: 'advanced' };
        }

        rows.push(...flat);
    }

    return { kind: 'simple', match: 'all', rows };
};

/** The conditions to store for a set of rows. */
export const conditionsOfRows = (rows: any[], match: MatchMode): any[] =>
    match === 'any' && rows.length > 1
        ? [Condition.unmarshal({ conditionType: 'OrCondition', conditions: rows })]
        : rows;
